import React, { useEffect, useContext, useState, useRef, useMemo } from 'react';
import fetchHook from '../../hooks/fetchMSSQL-hook';
import { NavigationContext } from '../../context/navigation-context';
import { NotificationManager } from 'react-notifications';
import { LangContext } from '../../context/lang-context';

export default function Permissions() {
    const { fetchMssqlApi } = fetchHook();
    const { TRL_Pack } = useContext(LangContext);
    const { setHeaderData, navlinks } = useContext(NavigationContext);

    const [rolePermissions, setRolePermissions] = useState([]);
    const [permissionsList, setPermissionsList] = useState([]);
    const [permissions, setPermissions] = useState([]);
    const [initialRolePermissions, setInitialRolePermissions] = useState([]);
    const [changedPermissions, setChangedPermissions] = useState([]);
    const [changedPermissionsOrder, setChangedPermissionsOrder] = useState([]);
    const [changed, setChanged] = useState(false);
    const [section, setSection] = useState('Permissions');

    const [drag, setDrag] = useState({ active: false, from: -1, over: -1 });
    const tbodyRef = useRef(null);
    const scrollRef = useRef(null);
    const ghostRef = useRef(null);
    const dragGeomRef = useRef({ y0: 0, top0: 0, left0: 0, width: 0, height: 0 });

    const permissionMap = useMemo(() => {
        const map = new Map();
        permissionsList.forEach(item => {
            map.set(item.Name, item);
        });
        return map;
    }, [permissionsList]);

    const defaultPermission = useMemo(() => {
        const map = new Map();
        permissionsList.forEach(({ PermissionId, Name }) => {
            map.set(Name, PermissionId);
        });
        return map;
    }, [permissionsList]);

    const getRolePermissions = () => {
        fetchMssqlApi('role-permissions', {}, res => {
            setInitialRolePermissions(Array.isArray(res) ? res : []);
        });
    };

    const getPermissionsList = () => {
        fetchMssqlApi('permissions', {}, res => {
            setPermissionsList(Array.isArray(res) ? res : []);
        });
    };

    const getData = (type = 'Permissions') => {
        const result = [];

        if (!rolePermissions?.length) return [];

        const permissionsNameArr = Object.keys(rolePermissions[0]).filter(
            key => key.startsWith('VD_')
        );

        if (!permissionsNameArr.length) {
            NotificationManager.error("Can't get permission name");
            return [];
        }

        permissionsNameArr.forEach(permName => {
            const permissionMeta = permissionMap.get(permName);
            const name = permissionMeta?.Description || permName;

            if (type === 'Permissions') {
                result.push({
                    DbName: permName,
                    Items: rolePermissions.map(role => ({
                        RoleId: role.RoleId,
                        Value: !!role[permName],
                        RoleName: role.Name
                    })),
                    Name: name
                });
            } else if (type === 'MenuOrder') {
                const menuLink = navlinks.find(link => link.text === name);

                result.push({
                    DbName: permName,
                    Name: name,
                    PermissionId: permissionMeta?.PermissionId || null,
                    MenuOrder: permissionMeta?.MenuOrder || null,
                    Icon: menuLink?.icon || null
                });
            }
        });

        if (type === 'Permissions') {
            return result;
        }

        return result
            .filter(item => item.PermissionId && Number(item.MenuOrder) > 0)
            .sort((a, b) => Number(a.MenuOrder) - Number(b.MenuOrder))
            .map((perm, idx) => ({ ...perm, MenuOrder: idx + 1 }));
    };

    const getHTMLFromBool = ({ Value, RoleId }, DbName) => (
        <input
            type="checkbox"
            checked={!!Value}
            onChange={() => {
                const permissionId = defaultPermission.get(DbName);
                if (!permissionId) return;

                setChanged(true);

                setPermissions(prev =>
                    prev.map(item =>
                        item.DbName === DbName
                            ? {
                                  ...item,
                                  Items: item.Items.map(roleItem =>
                                      roleItem.RoleId === RoleId
                                          ? { ...roleItem, Value: !roleItem.Value }
                                          : roleItem
                                  )
                              }
                            : item
                    )
                );

                setChangedPermissions(prev => {
                    const existingIndex = prev.findIndex(
                        item => item.PermissionId === permissionId && item.RoleId === RoleId
                    );

                    if (existingIndex === -1) {
                        return [...prev, { Value: !Value, PermissionId: permissionId, RoleId }];
                    }

                    return prev.filter(
                        item => !(item.PermissionId === permissionId && item.RoleId === RoleId)
                    );
                });
            }}
        />
    );

    const changeSection = data => {
        setSection(data);
    };

    const handleSubmit = () => {
        if (section === 'Permissions') {
            const payload = {
                rolePermissions: JSON.stringify(
                    changedPermissions.map(({ PermissionId, RoleId, Value }) => ({
                        PermissionId,
                        RoleId,
                        Value: Value ? 1 : 0
                    }))
                )
            };

            fetchMssqlApi('role-permissions', { method: 'PUT', data: payload }, () => {
                window.location.reload();
            });
        } else if (section === 'MenuOrder') {
            const payload = {
                menuOrder: JSON.stringify(
                    permissions.map(({ PermissionId, MenuOrder }) => ({
                        PermissionId,
                        MenuOrder
                    }))
                )
            };

            fetchMssqlApi('menu-order', { method: 'PUT', data: payload }, () => {
                window.location.reload();
            });
        }
    };

    useEffect(() => {
        setHeaderData({
            text: section === 'Permissions' ? 'Uprawnienia' : 'Kolejność menu'
        });
    }, [section, setHeaderData]);

    useEffect(() => {
        getRolePermissions();
        getPermissionsList();
    }, []);

    useEffect(() => {
        setRolePermissions(initialRolePermissions || []);
    }, [initialRolePermissions]);

    useEffect(() => {
        setPermissions(getData(section));
    }, [rolePermissions, section, permissionsList, navlinks]);

    useEffect(() => {
        if (!changedPermissions.length && !changedPermissionsOrder.length) {
            setChanged(false);
        }
    }, [changedPermissions, changedPermissionsOrder]);

    const onRowPointerDown = (e, idx) => {
        const isTouch = !!e.touches;
        const clientY = isTouch ? e.touches[0].clientY : e.clientY;
        if (!isTouch && e.button !== 0) return;

        const tr = e.currentTarget;
        const rect = tr.getBoundingClientRect();

        let holdTimer;
        let started = false;
        let rafId = null;

        const startDrag = () => {
            started = true;
            dragGeomRef.current = {
                y0: clientY,
                top0: rect.top,
                left0: rect.left,
                width: rect.width,
                height: rect.height,
                from: idx,
                over: idx
            };

            const ghost = tr.cloneNode(true);
            ghost.classList.add('perm-drag-ghost');
            Object.assign(ghost.style, {
                position: 'fixed',
                top: `${rect.top}px`,
                left: `${rect.left}px`,
                width: `${rect.width}px`,
                height: `${rect.height}px`,
                zIndex: 3000,
                pointerEvents: 'none',
                backgroundColor: '#fff',
                border: '1px solid #dee2e6',
                boxShadow: '0 8px 16px rgba(0,0,0,0.25)',
                borderRadius: '4px',
                transform: 'translateY(0)',
                willChange: 'transform',
                transition: 'none'
            });
            document.body.appendChild(ghost);
            ghostRef.current = ghost;

            tr.classList.add('perm-drag-hidden');
            setDrag({ active: true, from: idx, over: idx });
        };

        if (isTouch) {
            let moved = false;
            const cancel = () => {
                clearTimeout(holdTimer);
                moved = true;
            };
            e.target.addEventListener('touchmove', cancel, { once: true, passive: true });
            e.target.addEventListener('touchend', cancel, { once: true, passive: true });
            holdTimer = setTimeout(() => {
                if (!moved) requestAnimationFrame(() => startDrag());
            }, 220);
        } else {
            requestAnimationFrame(startDrag);
        }

        const onMove = ev => {
            if (ev.cancelable) ev.preventDefault();
            if (!started) return;

            const y = ev.touches ? ev.touches[0].clientY : ev.clientY;
            const dy = y - dragGeomRef.current.y0;

            if (ghostRef.current) {
                cancelAnimationFrame(rafId);
                rafId = requestAnimationFrame(() => {
                    ghostRef.current.style.transform = `translateY(${dy}px)`;
                });
            }

            const scrollMargin = 140;
            const maxSpeed = 200;
            const acceleration = 0.25;

            if (scrollRef.current) {
                const box = scrollRef.current.getBoundingClientRect();
                const distanceFromTop = y - box.top;
                const distanceFromBottom = box.bottom - y;

                if (distanceFromTop < scrollMargin) {
                    const speed = Math.max(
                        -maxSpeed,
                        -((scrollMargin - distanceFromTop) * acceleration)
                    );
                    scrollRef.current.scrollTop += speed;
                } else if (distanceFromBottom < scrollMargin) {
                    const speed = Math.min(
                        maxSpeed,
                        (scrollMargin - distanceFromBottom) * acceleration
                    );
                    scrollRef.current.scrollTop += speed;
                }
            }

            const rows = Array.from(tbodyRef.current.querySelectorAll('tr.perm-dr-row'));
            let over = rows.length;

            for (let i = 0; i < rows.length; i++) {
                const r = rows[i].getBoundingClientRect();
                const mid = r.top + r.height / 2;
                if (y < mid) {
                    over = i;
                    break;
                }
            }

            setDrag(d => {
                const newState = d.over === over ? d : { ...d, over };
                dragGeomRef.current.over = newState.over;
                return newState;
            });
        };

        const onUp = () => {
            clearTimeout(holdTimer);
            cancelAnimationFrame(rafId);
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
            document.removeEventListener('touchmove', onMove);
            document.removeEventListener('touchend', onUp);

            if (ghostRef.current) {
                ghostRef.current.remove();
                ghostRef.current = null;
            }
            tr.classList.remove('perm-drag-hidden');

            if (!started) return;

            setPermissions(prev => {
                const list = [...prev]
                    .filter(i => i.MenuOrder > 0)
                    .sort((a, b) => a.MenuOrder - b.MenuOrder);

                let to = dragGeomRef.current.over;
                const from = dragGeomRef.current.from;
                const [moved] = list.splice(from, 1);

                if (to > from) to -= 1;
                list.splice(to, 0, moved);

                const reordered = list.map((it, i) => ({
                    ...it,
                    MenuOrder: i + 1
                }));

                setChanged(true);
                setChangedPermissionsOrder(
                    reordered.map(({ PermissionId, MenuOrder }) => ({ PermissionId, MenuOrder }))
                );

                return reordered;
            });

            setDrag({ active: false, from: -1, over: -1 });
        };

        document.addEventListener('mousemove', onMove, { passive: false });
        document.addEventListener('mouseup', onUp, { passive: true });
        document.addEventListener('touchmove', onMove, { passive: false });
        document.addEventListener('touchend', onUp, { passive: true });
    };

    return (
        <div>
            <div className="m-20">
                <ul className="nav nav-tabs machine-tabs mb-3">
                    <li className="nav-item">
                        <button
                            className={`nav-link btn ${section === 'Permissions' ? 'active' : ''}`}
                            onClick={() => {
                                if (section !== 'Permissions') {
                                    setChangedPermissionsOrder([]);
                                    setChangedPermissions([]);
                                    setPermissions([]);
                                    setChanged(false);
                                }
                                changeSection('Permissions');
                            }}
                            tabIndex="0"
                        >
                            Uprawnienia
                        </button>
                    </li>
                    <li className="nav-item">
                        <button
                            className={`nav-link btn ${section === 'MenuOrder' ? 'active' : ''}`}
                            onClick={() => {
                                if (section !== 'MenuOrder') {
                                    setChangedPermissionsOrder([]);
                                    setPermissions([]);
                                    setChanged(false);
                                }
                                changeSection('MenuOrder');
                            }}
                            tabIndex="0"
                        >
                            Kolejność menu
                        </button>
                    </li>
                </ul>
            </div>

            {section === 'Permissions' ? (
                <div className="card">
                    <div className="card-header">
                        {!changed ? (
                            <div className="h5 mb-2 mt-2 text-center">Uprawnienia R24 Dashboard</div>
                        ) : (
                            <div className="d-flex justify-content-between align-items-center pb-0">
                                <div className="h5 mb-2 mt-2">Uprawnienia R24 Dashboard</div>
                                <button className="btn btn-success btn-sm mr-5" onClick={handleSubmit}>
                                    Zapisz
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="overflow-auto custom-scroll" style={{ maxHeight: '65vh' }}>
                        <table className="table table-striped">
                            <thead>
                                <tr className="text-center headers-color pe-none">
                                    <th>Nazwa uprawnienia</th>
                                    {rolePermissions?.map((perm, idx) => (
                                        <th key={idx}>{perm.Name}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {permissions?.map(
                                    permission =>
                                        permission.Name && (
                                            <tr key={permission.DbName} className="mt-2 mb-2 text-center">
                                                <td>{permission.Name || permission.DbName}</td>
                                                {permission.Items.map(item => (
                                                    <td key={item.RoleId}>
                                                        <div className="d-flex justify-content-center align-content-center mb-2 mt-2">
                                                            {getHTMLFromBool(item, permission.DbName)}
                                                        </div>
                                                    </td>
                                                ))}
                                            </tr>
                                        )
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : (
                <div className="card">
                    <div className="perm-card-header card-header d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center">
                            <button
                                className="btn btn-outline-secondary btn-sm"
                                onClick={() => {
                                    const sorted = [...permissions]
                                        .filter(i => i.MenuOrder > 0)
                                        .sort((a, b) => a.Name.localeCompare(b.Name, 'pl'))
                                        .map((it, i) => ({ ...it, MenuOrder: i + 1 }));

                                    setPermissions(sorted);
                                    setChangedPermissionsOrder(
                                        sorted.map(({ PermissionId, MenuOrder }) => ({
                                            PermissionId,
                                            MenuOrder
                                        }))
                                    );
                                    setChanged(true);
                                }}
                            >
                                <i className="fa fa-sort-alpha-asc" /> Sortuj alfabetycznie
                            </button>
                        </div>

                        <div className="h5 mb-0 text-center flex-grow-1">
                            Kolejność menu R24 Dashboard
                        </div>

                        {changed ? (
                            <button className="btn btn-success btn-sm mr-1" onClick={handleSubmit}>
                                <i className="fa fa-save mr-1" /> Zapisz
                            </button>
                        ) : (
                            <button className="btn btn-secondary btn-sm mr-1" disabled>
                                <i className="fa fa-check mr-1" /> Aktualne
                            </button>
                        )}
                    </div>

                    <div
                        className="perm-card-body card-body overflow-auto menuorder-body"
                        style={{ maxHeight: '60vh' }}
                        ref={scrollRef}
                    >
                        <table className="table table-striped mb-0">
                            <thead>
                                <tr>
                                    <th
                                        className="text-center"
                                        style={{ fontSize: '25px', pointerEvents: 'none' }}
                                    >
                                        Kolejność
                                    </th>
                                    <th
                                        className="text-center"
                                        style={{ fontSize: '25px', pointerEvents: 'none' }}
                                    >
                                        Nazwa
                                    </th>
                                    <th
                                        className="text-center"
                                        style={{ fontSize: '25px', pointerEvents: 'none' }}
                                    >
                                        Ikona
                                    </th>
                                </tr>
                            </thead>

                            <tbody ref={tbodyRef}>
                                {(() => {
                                    const base = [...permissions]
                                        .filter(i => i.MenuOrder > 0)
                                        .sort((a, b) => a.MenuOrder - b.MenuOrder);

                                    return (
                                        <>
                                            {base.map((permission, idx) => (
                                                <React.Fragment key={permission.PermissionId}>
                                                    {drag.active && drag.over === idx && (
                                                        <tr className="perm-drag-placeholder">
                                                            <td colSpan="3">
                                                                <div
                                                                    style={{
                                                                        height:
                                                                            dragGeomRef.current.height || 56
                                                                    }}
                                                                />
                                                            </td>
                                                        </tr>
                                                    )}

                                                    <tr
                                                        className="perm-dr-row text-center align-middle"
                                                        data-index={idx}
                                                        onMouseDown={e => onRowPointerDown(e, idx)}
                                                        onTouchStart={e => onRowPointerDown(e, idx)}
                                                        style={{
                                                            height: '7vh',
                                                            cursor: 'grab',
                                                            userSelect: 'none'
                                                        }}
                                                    >
                                                        <td
                                                            className="text-center"
                                                            style={{ fontSize: '20px' }}
                                                        >
                                                            <div className="ml-5">
                                                                {permission.MenuOrder}
                                                            </div>
                                                        </td>
                                                        <td style={{ fontSize: '20px' }}>
                                                            {permission.Name}
                                                        </td>
                                                        <td>
                                                            <i
                                                                className={`mr-3 ${permission.Icon || ''}`}
                                                                style={{
                                                                    width: 25,
                                                                    fontSize: '1.125em'
                                                                }}
                                                            />
                                                        </td>
                                                    </tr>
                                                </React.Fragment>
                                            ))}

                                            {drag.active && drag.over === base.length && (
                                                <tr className="perm-drag-placeholder">
                                                    <td colSpan="3">
                                                        <div
                                                            style={{
                                                                height: dragGeomRef.current.height || 56
                                                            }}
                                                        />
                                                    </td>
                                                </tr>
                                            )}
                                        </>
                                    );
                                })()}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}